/**
 * Cabinet-run drawing overlay.
 *
 * Canvas drawing for the cabinet-run-in-progress preview. Unlike the room
 * polygon, a cabinet run is an OPEN polyline along walls (no fill, no close).
 * The solid line follows `pathPoints` — the clicked points plus the inferred
 * corner at each wall transition — so an L reads as an L, not a diagonal.
 * Operates on a 2D canvas context already in world-space transform.
 */

import { drawSnapIndicator } from './roomDrawingUtils';
import { beginScreenSpaceOverlay } from './canvasUtils';
import { buildRunSegments } from '@shared-lib/cabinet-runs/buildRunSegments';
import { buildSegmentWallResolver } from '@shared-lib/cabinet-runs/segmentWall';
import { tooSharpBends, turnDeg } from '@shared-lib/cabinet-runs/bendGeometry';

const RUN_COLOR = '#B45309'; // amber-800 — distinct from room-drawing blue
const PREVIEW_COLOR = '#F59E0B'; // amber-500
// A leg the click would refuse (a bend over 90°) — red-600.
const TOO_SHARP_COLOR = '#DC2626';
const VERTEX_RADIUS = 0.08; // meters
const LABEL_OFFSET_PX = 14;

const unit = (x, z) => {
  const len = Math.hypot(x, z);
  return len < 1e-9 ? null : { x: x / len, z: z / len };
};

/**
 * The angle shown beside the cursor while drawing (design 2026-11-03): the
 * turn from the last placed leg to the leg the cursor would add, or — for the
 * first leg — its angle to the reference line (folded onto the line, so a leg
 * leaving a wall at 135° reads 45°). Null when there is nothing to measure.
 *
 * @param {Array<{x:number,z:number}>} pathPoints  the placed path
 * @param {{x:number,z:number}|null} cursorPos
 * @param {{x:number,z:number}|null} refDir  `cabinetRunSnap.referenceDirection`
 * @returns {{ angleDeg:number }|null}
 */
export function previewBend(pathPoints, cursorPos, refDir) {
  const path = pathPoints || [];
  if (!cursorPos || path.length === 0) return null;
  const last = path[path.length - 1];
  const next = unit(cursorPos.x - last.x, cursorPos.z - last.z);
  if (!next) return null;
  if (path.length >= 2) {
    const prev = path[path.length - 2];
    const inDir = unit(last.x - prev.x, last.z - prev.z);
    return inDir ? { angleDeg: Math.abs(turnDeg(inDir, next)) } : null;
  }
  const ref = refDir ? unit(refDir.x, refDir.z) : null;
  if (!ref) return null;
  const a = Math.abs(turnDeg(ref, next));
  return { angleDeg: Math.min(a, 180 - a) };
}

/**
 * Would placing `candidate` after `vertices` make a bend the draw tool
 * refuses — over 90° with a free leg in it? The ONE answer the click and the
 * preview both read: it builds the segments exactly as `closeRun` will
 * (`buildRunSegments` over the room's own walls) and asks `tooSharpBends`.
 *
 * @param {Array<{wallId:string|null, point:{x,z}}>} vertices
 * @param {{wallId:string|null, point:{x,z}}} candidate
 * @param {Object} room
 * @returns {boolean}
 */
export function candidateTooSharp(vertices, candidate, room) {
  if (!candidate || !room) return false;
  const resolver = buildSegmentWallResolver(room);
  const all = [...(vertices || []), candidate];
  const clicks = all.map((v) => ({
    wall: v.wallId ? resolver.resolve({ wallId: v.wallId }) : null,
    point: v.point,
  }));
  if (all.some((v, i) => v.wallId && !clicks[i].wall)) {
    return false; // a wall we cannot resolve — closeRun reports that, not us
  }
  return tooSharpBends(buildRunSegments(clicks)).length > 0;
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {Array<{x:number,z:number}>} pathPoints  run path incl. inferred corners
 * @param {Array<{wallId:string, point:{x:number,z:number}}>} vertices  clicked points
 * @param {{x:number,z:number}|null} cursorPos
 * @param {{type:string, position:{x:number,z:number}}|null} snapInfo
 * @param {number} scale
 * @param {{ angleDeg?:number|null, tooSharp?:boolean, view?:Object }} [bend]
 *   the readout beside the cursor; `view` is the plan's `{ width, height,
 *   pan, scale, rotation }` for the screen-space label
 */
export function drawCabinetRunPreview(
  ctx,
  pathPoints,
  vertices,
  cursorPos,
  snapInfo,
  scale,
  bend = {}
) {
  const path = pathPoints || [];
  const dots = (vertices || []).map((v) => v.point);
  const lineWidth = 2 / scale;
  const dashSize = 0.1;

  ctx.save();

  // Placed path — follows the inferred corners (solid).
  if (path.length >= 2) {
    ctx.strokeStyle = RUN_COLOR;
    ctx.lineWidth = lineWidth;
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.moveTo(path[0].x, path[0].z);
    for (let i = 1; i < path.length; i++) ctx.lineTo(path[i].x, path[i].z);
    ctx.stroke();
  }

  // Dashed preview from the last placed point to the cursor.
  if (cursorPos && path.length > 0) {
    const last = path[path.length - 1];
    ctx.strokeStyle = bend.tooSharp ? TOO_SHARP_COLOR : PREVIEW_COLOR;
    ctx.lineWidth = lineWidth;
    ctx.setLineDash([dashSize, dashSize]);
    ctx.beginPath();
    ctx.moveTo(last.x, last.z);
    ctx.lineTo(cursorPos.x, cursorPos.z);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  // Clicked vertices as dots.
  for (const p of dots) {
    ctx.beginPath();
    ctx.arc(p.x, p.z, VERTEX_RADIUS, 0, Math.PI * 2);
    ctx.fillStyle = RUN_COLOR;
    ctx.fill();
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = lineWidth * 0.6;
    ctx.stroke();
  }

  ctx.restore();

  if (cursorPos && bend.view && Number.isFinite(bend.angleDeg)) {
    drawBendLabel(ctx, cursorPos, bend);
  }

  // Snap ring — reuse the room-drawing indicator.
  if (snapInfo && snapInfo.position) {
    drawSnapIndicator(
      ctx,
      snapInfo.position,
      snapInfo.type,
      scale,
      snapInfo.target
    );
  }
}

/**
 * The angle beside the cursor, in SCREEN space (text is sized in pixels, not
 * metres) through the one door every overlay uses.
 */
function drawBendLabel(ctx, cursorPos, { angleDeg, tooSharp, view }) {
  const toScreen = beginScreenSpaceOverlay(ctx, view);
  const p = toScreen(cursorPos.x, cursorPos.z);
  const text = `${Math.round(angleDeg)}°`;
  ctx.font = '600 12px system-ui, sans-serif';
  ctx.textBaseline = 'middle';
  const x = p.x + LABEL_OFFSET_PX;
  const y = p.y - LABEL_OFFSET_PX;
  ctx.lineWidth = 3;
  ctx.strokeStyle = '#FFFFFF';
  ctx.strokeText(text, x, y);
  ctx.fillStyle = tooSharp ? TOO_SHARP_COLOR : RUN_COLOR;
  ctx.fillText(text, x, y);
  ctx.restore();
}
